import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { TeamPlan } from './useTeams';

export interface AdminTeam {
  id: string;
  name: string;
  plan: TeamPlan;
  created_at: string;
  billing_exempt: boolean;
  subscription_status: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
}

interface AdminTeamsResponse {
  teams: AdminTeam[];
}

interface AdminCheckResponse {
  is_admin: boolean;
}

interface UpdateTeamRequest {
  billing_exempt?: boolean;
}

export function useAdminCheck() {
  const query = useQuery({
    queryKey: ['admin', 'check'],
    queryFn: async () => {
      const response = await apiClient.get<AdminCheckResponse>('/api/v1/admin/check');
      return response.data.is_admin;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    isAdmin: query.data ?? false,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useAdminTeams() {
  const queryClient = useQueryClient();

  const teamsQuery = useQuery({
    queryKey: ['admin', 'teams'],
    queryFn: async () => {
      const response = await apiClient.get<AdminTeamsResponse>('/api/v1/admin/teams');
      return response.data.teams;
    },
    staleTime: 1000 * 60, // 1 minute
  });

  const updateTeamMutation = useMutation({
    mutationFn: async ({ teamId, data }: { teamId: string; data: UpdateTeamRequest }) => {
      const response = await apiClient.patch<AdminTeam>(`/api/v1/admin/teams/${teamId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });

  const toggleBillingExempt = async (teamId: string, exempt: boolean) => {
    return updateTeamMutation.mutateAsync({ teamId, data: { billing_exempt: exempt } });
  };

  return {
    teams: teamsQuery.data ?? [],
    isLoading: teamsQuery.isLoading,
    error: teamsQuery.error,
    refetch: teamsQuery.refetch,

    toggleBillingExempt,
    isUpdating: updateTeamMutation.isPending,
    updateError: updateTeamMutation.error,
  };
}
